/**
 * H5.2 — Domain Registration & DNS Management
 *
 * Three tabs:
 *  - My Domains   — list of registered domains
 *  - Register     — domain search + registration form
 *  - DNS Manager  — per-domain DNS record CRUD
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
    Globe,
    Search,
    Plus,
    Trash2,
    CheckCircle,
    AlertCircle,
    Info,
    RefreshCw,
    Calendar,
    RotateCcw,
    Mail,
    Forward,
} from 'lucide-react';
import {
    GlassCard,
    GlassButton,
    GlassInput,
    GlassSelect,
    GlassTabs,
    GlassBadge,
    ModuleHeader,
} from '../ui';
import {
    searchDomains,
    registerDomain,
    getDomains,
    updateDomain,
    deleteDomain,
    addDnsRecord,
    deleteDnsRecord,
    pointDomainToAime,
    pointDomainToWordPress,
    setupEmailRouting,
    disableEmailRouting,
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './Domains.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DomainResult {
    domain: string;
    available: boolean;
    price: number | null;
    currency: string;
}

interface DnsRecord {
    record_id: string;
    type: string;
    name: string;
    content: string;
    ttl: number;
    note?: string;
    created_at?: string;
}

interface DomainRecord {
    domain_id: string;
    domain_name: string;
    registrar: string;
    registrar_domain_id: string | null;
    registered_at: string;
    expires_at: string | null;
    auto_renew: boolean;
    status: string;
    dns_records: DnsRecord[];
    stub_mode: boolean;
    email_routing_enabled?: boolean;
    email_forward_to?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS = [
    { id: 'my-domains', label: 'My Domains' },
    { id: 'register', label: 'Register Domain' },
    { id: 'dns', label: 'DNS Manager' },
];

const DNS_TYPES = [
    { value: 'A',     label: 'A' },
    { value: 'CNAME', label: 'CNAME' },
    { value: 'MX',    label: 'MX' },
    { value: 'TXT',   label: 'TXT' },
    { value: 'AAAA',  label: 'AAAA' },
    { value: 'NS',    label: 'NS' },
];

const MAX_DOMAINS = 5;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusVariant(status: string): 'success' | 'warning' | 'danger' | 'info' | 'default' {
    switch (status) {
        case 'active':    return 'success';
        case 'pending':   return 'info';
        case 'expired':   return 'warning';
        case 'failed':
        case 'cancelled': return 'danger';
        default:          return 'default';
    }
}

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// Sub-component: My Domains tab
// ---------------------------------------------------------------------------

interface MyDomainsProps {
    domains: DomainRecord[];
    loading: boolean;
    stubMode: boolean;
    onAutoRenewToggle: (id: string, value: boolean) => void;
    onDelete: (id: string) => void;
    onManageDns: (id: string) => void;
    onPointToAime: (id: string) => void;
    onPointToWordPress: (id: string) => void;
    onGoRegister: () => void;
    onEmailRoutingToggle: (id: string, enabled: boolean) => void;
}

const MyDomainsTab: React.FC<MyDomainsProps> = ({
    domains,
    loading,
    stubMode,
    onAutoRenewToggle,
    onDelete,
    onManageDns,
    onPointToAime,
    onPointToWordPress,
    onGoRegister,
    onEmailRoutingToggle,
}) => {
    const active = domains.filter(d => d.status !== 'cancelled');

    if (loading) {
        return <div className="domains-empty-state"><p>Loading domains...</p></div>;
    }

    if (active.length === 0) {
        return (
            <div className="domains-empty-state">
                <div className="domains-empty-state-icon">
                    <Globe size={48} />
                </div>
                <h3>No domains registered yet</h3>
                <p>Search and register a domain to get started.</p>
                <GlassButton variant="primary" onClick={onGoRegister}>
                    <Plus size={16} />
                    Register a Domain
                </GlassButton>
            </div>
        );
    }

    return (
        <>
            {stubMode && (
                <div className="stub-notice">
                    <Info size={13} />
                    Demo mode — CLOUDFLARE_API_TOKEN not configured. Domains are stored locally only.
                </div>
            )}
            <div className="domain-limit-row">
                <Globe size={14} />
                <span>{active.length} of {MAX_DOMAINS} domains used</span>
                <div className="domain-limit-bar-wrap">
                    <div
                        className="domain-limit-bar"
                        style={{ width: `${(active.length / MAX_DOMAINS) * 100}%` }}
                    />
                </div>
            </div>
            <div className="domains-list">
                {active.map(d => (
                    <GlassCard key={d.domain_id} className="domain-card">
                        <div className="domain-card-header">
                            <div className="domain-card-name">
                                <Globe size={16} />
                                {d.domain_name}
                            </div>
                            <div className="domain-card-badges">
                                <GlassBadge variant={statusVariant(d.status)}>
                                    {d.status}
                                </GlassBadge>
                                {d.stub_mode && (
                                    <GlassBadge variant="info">demo</GlassBadge>
                                )}
                            </div>
                        </div>

                        <div className="domain-card-meta">
                            <span>
                                <Calendar size={12} />
                                Registered {formatDate(d.registered_at)}
                            </span>
                            {d.expires_at && (
                                <span>
                                    <Calendar size={12} />
                                    Expires {formatDate(d.expires_at)}
                                </span>
                            )}
                            <span>Registrar: {d.registrar}</span>
                        </div>

                        {/* Email Routing */}
                        <div className="domain-card-email-routing">
                            <label className="auto-renew-toggle">
                                <input
                                    type="checkbox"
                                    checked={d.email_routing_enabled || false}
                                    onChange={e => onEmailRoutingToggle(d.domain_id, e.target.checked)}
                                />
                                <Mail size={14} />
                                Email forwarding
                            </label>
                            {d.email_routing_enabled && d.email_forward_to && (
                                <span className="email-routing-dest">
                                    <Forward size={12} />
                                    info@{d.domain_name} &rarr; {d.email_forward_to}
                                </span>
                            )}
                        </div>

                        <div className="domain-card-actions">
                            <label className="auto-renew-toggle">
                                <input
                                    type="checkbox"
                                    checked={d.auto_renew}
                                    onChange={e => onAutoRenewToggle(d.domain_id, e.target.checked)}
                                />
                                Auto-renew
                            </label>

                            <GlassButton
                                variant="secondary"
                                size="sm"
                                onClick={() => onManageDns(d.domain_id)}
                            >
                                Manage DNS
                            </GlassButton>

                            <GlassButton
                                variant="secondary"
                                size="sm"
                                onClick={() => onPointToAime(d.domain_id)}
                            >
                                Point to AiMe
                            </GlassButton>

                            <GlassButton
                                variant="secondary"
                                size="sm"
                                onClick={() => onPointToWordPress(d.domain_id)}
                            >
                                Point to WordPress
                            </GlassButton>

                            <GlassButton
                                variant="danger"
                                size="sm"
                                onClick={() => onDelete(d.domain_id)}
                            >
                                <Trash2 size={14} />
                                Remove
                            </GlassButton>
                        </div>
                    </GlassCard>
                ))}
            </div>
        </>
    );
};

// ---------------------------------------------------------------------------
// Sub-component: Register Domain tab
// ---------------------------------------------------------------------------

interface RegisterTabProps {
    domains: DomainRecord[];
    onRegistered: (d: DomainRecord) => void;
}

const RegisterTab: React.FC<RegisterTabProps> = ({ domains, onRegistered }) => {
    const { user } = useAuth();

    const [query, setQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [results, setResults] = useState<DomainResult[]>([]);
    const [stubMode, setStubMode] = useState(false);
    const [searched, setSearched] = useState(false);

    // Registration form state
    const [selectedDomain, setSelectedDomain] = useState('');
    const [regName,    setRegName]    = useState((user as any)?.full_name || '');
    const [regEmail,   setRegEmail]   = useState((user as any)?.email || '');
    const [regPhone,   setRegPhone]   = useState('');
    const [regAddress, setRegAddress] = useState('');
    const [registering, setRegistering] = useState(false);
    const [regError, setRegError] = useState('');
    const [success, setSuccess] = useState<DomainRecord | null>(null);

    const activeDomainCount = domains.filter(d => d.status !== 'cancelled').length;

    const handleSearch = async () => {
        const q = query.trim();
        if (!q) return;
        setSearching(true);
        setSearchError('');
        setResults([]);
        setSearched(false);
        setSelectedDomain('');
        try {
            const data = await searchDomains(q);
            setResults(data.available || []);
            setStubMode(!!data.stub_mode);
            setSearched(true);
        } catch (e: any) {
            setSearchError(getApiError(e, 'Search failed. Please try again.'));
        } finally {
            setSearching(false);
        }
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch();
    };

    const handleRegister = async () => {
        if (!selectedDomain) return;
        if (!regName || !regEmail) {
            setRegError('Name and email are required.');
            return;
        }
        setRegistering(true);
        setRegError('');
        try {
            const data = await registerDomain({
                domain_name: selectedDomain,
                registrant: { name: regName, email: regEmail, phone: regPhone, address: regAddress },
            });
            setSuccess(data);
            onRegistered(data);
        } catch (e: any) {
            setRegError(getApiError(e, 'Registration failed. Please try again.'));
        } finally {
            setRegistering(false);
        }
    };

    if (success) {
        return (
            <GlassCard>
                <div className="register-success">
                    <div className="register-success-icon">
                        <CheckCircle size={40} />
                    </div>
                    <h3>Domain Registered</h3>
                    <p>{success.domain_name} has been successfully registered.</p>
                    <GlassButton
                        variant="primary"
                        onClick={() => {
                            setSuccess(null);
                            setSelectedDomain('');
                            setQuery('');
                            setResults([]);
                            setSearched(false);
                        }}
                    >
                        Register Another
                    </GlassButton>
                </div>
            </GlassCard>
        );
    }

    return (
        <>
            {activeDomainCount >= MAX_DOMAINS && (
                <GlassCard style={{ marginBottom: 16 }}>
                    <div className="stub-notice" style={{ marginBottom: 0 }}>
                        <AlertCircle size={13} />
                        Domain limit reached ({MAX_DOMAINS} of {MAX_DOMAINS}). Remove an existing domain to register a new one.
                    </div>
                </GlassCard>
            )}

            <GlassCard>
                <div className="domain-limit-row" style={{ marginBottom: 12 }}>
                    <Globe size={14} />
                    <span>{activeDomainCount} of {MAX_DOMAINS} domains used</span>
                    <div className="domain-limit-bar-wrap">
                        <div
                            className="domain-limit-bar"
                            style={{ width: `${(activeDomainCount / MAX_DOMAINS) * 100}%` }}
                        />
                    </div>
                </div>

                {/* Wrap in div to catch keydown since GlassInput doesn't expose onKeyDown */}
                <div onKeyDown={handleSearchKeyDown}>
                    <div className="domain-search-row">
                        <GlassInput
                            placeholder="Search for a domain name (e.g. mybusiness)"
                            value={query}
                            onChange={setQuery}
                        />
                        <GlassButton
                            variant="primary"
                            onClick={handleSearch}
                            disabled={searching || !query.trim() || activeDomainCount >= MAX_DOMAINS}
                        >
                            {searching
                                ? <><RefreshCw size={15} /> Searching...</>
                                : <><Search size={15} /> Search</>
                            }
                        </GlassButton>
                    </div>
                </div>

                {searchError && (
                    <div className="stub-notice" style={{ color: 'var(--danger, #dc2626)' }}>
                        <AlertCircle size={13} />
                        {searchError}
                    </div>
                )}

                {searched && stubMode && (
                    <div className="stub-notice">
                        <Info size={13} />
                        Demo mode — showing mock availability data. Configure CLOUDFLARE_API_TOKEN for live results.
                    </div>
                )}

                {results.length > 0 && (
                    <div className="domain-results-list">
                        {results.map(r => (
                            <div key={r.domain} className="domain-result-row">
                                <span className="domain-result-name">{r.domain}</span>
                                {r.price != null && (
                                    <span className="domain-result-price">
                                        {r.currency} {r.price.toFixed(2)}/yr
                                    </span>
                                )}
                                <div className="domain-result-actions">
                                    <GlassBadge variant={r.available ? 'success' : 'danger'}>
                                        {r.available ? 'Available' : 'Taken'}
                                    </GlassBadge>
                                    {r.available && (
                                        <GlassButton
                                            variant="primary"
                                            size="sm"
                                            disabled={activeDomainCount >= MAX_DOMAINS}
                                            onClick={() => setSelectedDomain(r.domain)}
                                        >
                                            Register
                                        </GlassButton>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </GlassCard>

            {selectedDomain && (
                <GlassCard className="register-form-card" style={{ marginTop: 16 }}>
                    <div className="register-form-heading">Registrant Details</div>
                    <div className="register-form-domain-badge">
                        <Globe size={14} />
                        {selectedDomain}
                    </div>
                    <div className="register-form-grid">
                        <GlassInput
                            label="Full Name *"
                            value={regName}
                            onChange={setRegName}
                            placeholder="Jane Smith"
                        />
                        <GlassInput
                            label="Email *"
                            value={regEmail}
                            onChange={setRegEmail}
                            placeholder="jane@example.com"
                            type="email"
                        />
                        <GlassInput
                            label="Phone"
                            value={regPhone}
                            onChange={setRegPhone}
                            placeholder="+61 400 000 000"
                            type="tel"
                        />
                        <GlassInput
                            label="Address"
                            value={regAddress}
                            onChange={setRegAddress}
                            placeholder="123 Main St, Sydney NSW 2000"
                        />
                    </div>

                    {regError && (
                        <div className="stub-notice" style={{ color: 'var(--danger, #dc2626)', marginBottom: 12 }}>
                            <AlertCircle size={13} />
                            {regError}
                        </div>
                    )}

                    <div className="register-form-actions">
                        <GlassButton
                            variant="secondary"
                            onClick={() => setSelectedDomain('')}
                        >
                            Cancel
                        </GlassButton>
                        <GlassButton
                            variant="primary"
                            onClick={handleRegister}
                            disabled={registering}
                        >
                            {registering
                                ? <><RefreshCw size={14} /> Registering...</>
                                : <><CheckCircle size={14} /> Confirm &amp; Register</>
                            }
                        </GlassButton>
                    </div>
                </GlassCard>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Sub-component: DNS Manager tab
// ---------------------------------------------------------------------------

interface DnsManagerProps {
    domains: DomainRecord[];
    initialDomainId?: string;
    onDnsUpdated: (domainId: string, records: DnsRecord[]) => void;
}

const DnsManagerTab: React.FC<DnsManagerProps> = ({
    domains,
    initialDomainId,
    onDnsUpdated,
}) => {
    const activeDomains = domains.filter(d => d.status !== 'cancelled');

    const [selectedId, setSelectedId] = useState(initialDomainId || activeDomains[0]?.domain_id || '');
    const [adding, setAdding] = useState(false);
    const [deleting, setDeleting] = useState<string | null>(null);

    // Add form state
    const [dnsType,    setDnsType]    = useState('A');
    const [dnsName,    setDnsName]    = useState('@');
    const [dnsContent, setDnsContent] = useState('');
    const [dnsTtl,     setDnsTtl]     = useState('3600');
    const [addError,   setAddError]   = useState('');

    // Sync selectedId when initialDomainId changes
    useEffect(() => {
        if (initialDomainId) setSelectedId(initialDomainId);
    }, [initialDomainId]);

    const selected = domains.find(d => d.domain_id === selectedId);

    const handleAdd = async () => {
        if (!selectedId || !dnsContent.trim()) {
            setAddError('Content is required.');
            return;
        }
        setAdding(true);
        setAddError('');
        try {
            const data = await addDnsRecord(selectedId, {
                type: dnsType,
                name: dnsName || '@',
                content: dnsContent.trim(),
                ttl: parseInt(dnsTtl, 10) || 3600,
            });
            onDnsUpdated(selectedId, data.dns_records || []);
            setDnsContent('');
            setDnsName('@');
        } catch (e: any) {
            setAddError(getApiError(e, 'Failed to add DNS record.'));
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async (recordId: string) => {
        if (!selectedId) return;
        setDeleting(recordId);
        try {
            const data = await deleteDnsRecord(selectedId, recordId);
            onDnsUpdated(selectedId, data.dns_records || []);
        } catch (e: any) {
            console.error('Failed to delete DNS record:', e);
        } finally {
            setDeleting(null);
        }
    };

    const handlePointToAime = async () => {
        if (!selectedId) return;
        try {
            const data = await pointDomainToAime(selectedId);
            onDnsUpdated(selectedId, data.dns_records || []);
        } catch (e: any) {
            console.error('Failed to point to AiMe:', e);
        }
    };

    const handlePointToWordPress = async () => {
        if (!selectedId) return;
        try {
            const data = await pointDomainToWordPress(selectedId);
            onDnsUpdated(selectedId, data.dns_records || []);
        } catch (e: any) {
            console.error('Failed to point to WordPress:', e);
        }
    };

    const domainOptions = activeDomains.map(d => ({ value: d.domain_id, label: d.domain_name }));

    if (activeDomains.length === 0) {
        return (
            <div className="domains-empty-state">
                <div className="domains-empty-state-icon"><Globe size={40} /></div>
                <h3>No domains yet</h3>
                <p>Register a domain first to manage DNS records.</p>
            </div>
        );
    }

    return (
        <>
            <div className="dns-manager-header">
                <GlassSelect
                    options={domainOptions}
                    value={selectedId}
                    onChange={setSelectedId}
                    placeholder="Select a domain..."
                />
            </div>

            {selectedId && (
                <>
                    <div className="dns-quick-actions">
                        <GlassButton variant="secondary" size="sm" onClick={handlePointToAime}>
                            <Globe size={14} />
                            Point to AiMe Pages
                        </GlassButton>
                        <GlassButton variant="secondary" size="sm" onClick={handlePointToWordPress}>
                            <RotateCcw size={14} />
                            Point to WordPress
                        </GlassButton>
                    </div>

                    <div className="dns-add-form">
                        <GlassSelect
                            options={DNS_TYPES}
                            value={dnsType}
                            onChange={setDnsType}
                        />
                        <GlassInput
                            placeholder="Name (e.g. @, www)"
                            value={dnsName}
                            onChange={setDnsName}
                        />
                        <GlassInput
                            placeholder="Content (IP or hostname)"
                            value={dnsContent}
                            onChange={setDnsContent}
                        />
                        <GlassInput
                            placeholder="TTL"
                            value={dnsTtl}
                            onChange={setDnsTtl}
                        />
                        <GlassButton
                            variant="primary"
                            onClick={handleAdd}
                            disabled={adding || !dnsContent.trim()}
                        >
                            {adding
                                ? <RefreshCw size={14} />
                                : <Plus size={14} />
                            }
                            Add
                        </GlassButton>
                    </div>

                    {addError && (
                        <div className="stub-notice" style={{ color: 'var(--danger, #dc2626)', marginBottom: 12 }}>
                            <AlertCircle size={13} />
                            {addError}
                        </div>
                    )}

                    <GlassCard>
                        {selected && selected.dns_records.length === 0 ? (
                            <div className="dns-empty-state">
                                <p>No DNS records yet. Add one above or use a quick action.</p>
                            </div>
                        ) : (
                            <div className="dns-records-table-wrap">
                                <table className="dns-records-table">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Name</th>
                                            <th>Content</th>
                                            <th>TTL</th>
                                            <th>Note</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(selected?.dns_records || []).map(r => (
                                            <tr key={r.record_id}>
                                                <td>
                                                    <span className="dns-record-type-badge">{r.type}</span>
                                                </td>
                                                <td>{r.name}</td>
                                                <td>{r.content}</td>
                                                <td>{r.ttl}</td>
                                                <td>{r.note || '—'}</td>
                                                <td>
                                                    <GlassButton
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDelete(r.record_id)}
                                                        disabled={deleting === r.record_id}
                                                        title="Delete record"
                                                    >
                                                        {deleting === r.record_id
                                                            ? <RefreshCw size={13} />
                                                            : <Trash2 size={13} />
                                                        }
                                                    </GlassButton>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </GlassCard>
                </>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Main module
// ---------------------------------------------------------------------------

const DomainsModule: React.FC = () => {
    const [activeTab,  setActiveTab]  = useState('my-domains');
    const [domains,    setDomains]    = useState<DomainRecord[]>([]);
    const [loading,    setLoading]    = useState(true);
    const [dnsTarget,  setDnsTarget]  = useState<string | undefined>(undefined);

    const stubMode = domains.some(d => d.stub_mode);

    const loadDomains = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getDomains();
            setDomains(data || []);
        } catch (e) {
            console.error('Failed to load domains:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDomains();
    }, [loadDomains]);

    const handleAutoRenewToggle = async (id: string, value: boolean) => {
        try {
            await updateDomain(id, { auto_renew: value });
            setDomains(prev => prev.map(d => d.domain_id === id ? { ...d, auto_renew: value } : d));
        } catch (e) {
            console.error('Failed to update auto_renew:', e);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Remove this domain from AiMe?')) return;
        try {
            await deleteDomain(id);
            setDomains(prev => prev.map(d => d.domain_id === id ? { ...d, status: 'cancelled' } : d));
        } catch (e) {
            console.error('Failed to delete domain:', e);
        }
    };

    const handleManageDns = (id: string) => {
        setDnsTarget(id);
        setActiveTab('dns');
    };

    const handlePointToAime = async (id: string) => {
        try {
            const data = await pointDomainToAime(id);
            handleDnsUpdated(id, data.dns_records || []);
        } catch (e) {
            console.error('Failed to point domain to AiMe:', e);
        }
    };

    const handlePointToWordPress = async (id: string) => {
        try {
            const data = await pointDomainToWordPress(id);
            handleDnsUpdated(id, data.dns_records || []);
        } catch (e) {
            console.error('Failed to point domain to WordPress:', e);
        }
    };

    const handleEmailRoutingToggle = async (id: string, enabled: boolean) => {
        try {
            if (enabled) {
                const result = await setupEmailRouting(id, {});
                setDomains(prev => prev.map(d => d.domain_id === id
                    ? { ...d, email_routing_enabled: true, email_forward_to: result.email_forward_to }
                    : d));
            } else {
                await disableEmailRouting(id);
                setDomains(prev => prev.map(d => d.domain_id === id
                    ? { ...d, email_routing_enabled: false, email_forward_to: undefined }
                    : d));
            }
        } catch (e) {
            console.error('Failed to toggle email routing:', e);
        }
    };

    const handleRegistered = (d: DomainRecord) => {
        setDomains(prev => [d, ...prev]);
        setActiveTab('my-domains');
    };

    const handleDnsUpdated = (domainId: string, records: DnsRecord[]) => {
        setDomains(prev =>
            prev.map(d => d.domain_id === domainId ? { ...d, dns_records: records } : d)
        );
    };

    const activeDomainCount = domains.filter(d => d.status !== 'cancelled').length;

    const headerActions: React.ReactNode[] = [
        <GlassButton
            key="refresh"
            variant="secondary"
            size="sm"
            onClick={loadDomains}
            title="Refresh"
        >
            <RefreshCw size={15} />
            Refresh
        </GlassButton>,
    ];

    return (
        <div className="domains-module">
            <ModuleHeader
                title="Domains"
                breadcrumb={['Domains', `${activeDomainCount} of ${MAX_DOMAINS} registered`]}
                actions={headerActions}
            />

            <div style={{ marginTop: 20 }}>
                <GlassTabs
                    tabs={TABS}
                    activeTab={activeTab}
                    onChange={setActiveTab}
                />
            </div>

            <div style={{ marginTop: 20 }}>
                {activeTab === 'my-domains' && (
                    <MyDomainsTab
                        domains={domains}
                        loading={loading}
                        stubMode={stubMode}
                        onAutoRenewToggle={handleAutoRenewToggle}
                        onDelete={handleDelete}
                        onManageDns={handleManageDns}
                        onPointToAime={handlePointToAime}
                        onPointToWordPress={handlePointToWordPress}
                        onGoRegister={() => setActiveTab('register')}
                        onEmailRoutingToggle={handleEmailRoutingToggle}
                    />
                )}

                {activeTab === 'register' && (
                    <RegisterTab
                        domains={domains}
                        onRegistered={handleRegistered}
                    />
                )}

                {activeTab === 'dns' && (
                    <DnsManagerTab
                        domains={domains}
                        initialDomainId={dnsTarget}
                        onDnsUpdated={handleDnsUpdated}
                    />
                )}
            </div>
        </div>
    );
};

export default DomainsModule;
